import type { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"

import { AgencySettingsNav } from "@/components/agency/settings/agency-settings-nav"
import Heading from "@/components/heading"
import { Separator } from "@/components/ui/separator"
import { cn } from "@/lib/utils"

/**
 * OKS-40 — Agency Settings shell layout.
 *
 * Mirrors `BrandSettingsLayout` exactly — same vertical sub-nav on
 * lg, stacked-above-content on mobile. Visual consistency across
 * Brand/Agency/Creator settings shells is intentional (UX Spec §1).
 */

interface AgencySettingsLayoutProps {
  /** Section heading. Defaults to the translated "Settings" string. */
  title?: string
  /** Optional subtitle. */
  description?: string
  /** OKS-672 — widen the content column for the relocated Team page
   *  (wide data table). Mirrors `BrandSettingsLayout`. Defaults to
   *  `false` (narrow form column). */
  wide?: boolean
}

export default function AgencySettingsLayout({
  children,
  title,
  description,
  wide = false,
}: PropsWithChildren<AgencySettingsLayoutProps>) {
  const { t } = useTranslation()

  return (
    <div data-testid="agency-settings-layout" className="px-4 py-6">
      <Heading
        title={title ?? t("settings.agency.nav.section")}
        description={description ?? ""}
      />

      <div className="flex flex-col space-y-8 lg:flex-row lg:space-y-0 lg:space-x-12">
        <aside className="w-full max-w-xl lg:w-48">
          <AgencySettingsNav />
        </aside>

        <Separator className="my-6 md:hidden" />

        <div className={cn("flex-1", { "md:max-w-2xl": !wide })}>
          <section className={cn("space-y-12", { "max-w-xl": !wide })}>
            {children}
          </section>
        </div>
      </div>
    </div>
  )
}
